import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Role } from '@lite-workspace/dto-auth';
import {
  activeClientSession,
  buildClientReentryUrl,
  clearClientPreview,
  readClientSession,
  releaseClientTabToCabinet,
} from './client-session';
import { MANAGER_ROLES, ProjectRoleService, sessionKnown } from './project-role';
import { buildCabinetDeniedUrl, CabinetDeniedReason } from './access-denied';
import { isBffMode, redirectToBffLogin } from './bff';
import { VerdictBudget } from './route-wait';
import { EXPECTED_FORBIDDEN } from './http-context.token';

/**
 * Cabinet routes are photographer-only, fail-closed: the guard lets a navigation through only once the
 * SERVER has confirmed a staff role — anything short of that (no staff role, an undecidable role load,
 * no session) is an exit, never a pass. One exception: a project route whose staff role is already in the
 * tab cache opens at once, and the server answer, fetched every entry, redirects late on refusal.
 * «Staff» on the server includes the account's main role, so a photographer with zero projects still
 * reaches the onboarding wizard. Two exits: a student with a saved link goes home to their own zone;
 * everyone else lands on the returnable denial screen (retry / re-login / «В мой альбом»), which this guard does not
 * cover — a loop through it is impossible by construction. A project the server does not know is no
 * denial at all: it renders the app's 404 page.
 *
 * Правило для любого маршрута, к которому подвешен гвард и в чьём адресе нет `:projectId`: проектной роли
 * там нет, поэтому судим по тому, есть ли у человека штабная роль хоть в одном проекте. Чистый ученик — не
 * пускаем; кто угодно со штабной ролью хоть где-нибудь — пускаем; доказать не удалось — отказ.
 * A route without this guard in `app.routes.ts` is outside this rule by construction; that file,
 * not this comment, is the single source of which routes are gated.
 */
export const clientCabinetGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const roleService = inject(ProjectRoleService);
  const budget = inject(VerdictBudget);
  const http = inject(HttpClient);

  // Набранный адрес кабинета закрывает просмотр «как клиент»: вкладка и так авторизована владельцем,
  // а живая запись увела бы его назад в галерею ученика. Снимаем ДО releaseClientTabToCabinet —
  // её sub равен sub владельца, и метка ученической вкладки пережила бы уход.
  clearClientPreview();

  // Заявка на кабинет — и есть сигнал «в этой вкладке теперь фотограф»: метку ученической вкладки
  // здесь снимает смена вошедшего, иначе сверка sub ниже никогда бы не вернулась.
  releaseClientTabToCabinet();

  const session = activeClientSession();
  if (session) {
    if (session.linkId) return router.createUrlTree(['/client', session.linkId, 'gallery']);
    return studentExit(router, state.url);
  }

  const projectId = route.paramMap.get('projectId');

  if (!sessionKnown()) return noSessionExit(router, state.url);

  if (projectId) {
    // Сервер спрашиваем на каждом входе: кэш пережил бы смену владельца куки соседней вкладкой.
    const verdict = roleService.loadRolesFresh(projectId).then(
      async (roles): Promise<true | UrlTree | RedirectCommand> =>
        isStaff(roles) ? true : projectRefusal(router, http, projectId, state.url),
      () => denied(router, state.url, 'roles-unavailable'),
    );
    if (!isStaff(roleService.cachedRoles(projectId) ?? [])) return verdict;
    // Штабная роль в кэше открывает проект сразу; свежий отказ догоняет и уводит тем же выходом, пока
    // человек в этом проекте, на каком бы разделе он ни был.
    const project = `/projects/${projectId}`;
    budget.applyLate(
      verdict.then(fresh => (fresh === true ? null : (): void => budget.redirect(fresh))),
      url => url === project || url.startsWith(`${project}/`) || url.startsWith(`${project}?`),
    );
    return true;
  }

  try {
    return (await roleService.loadStaffAnywhere()) ? true : studentExit(router, state.url);
  } catch {
    return denied(router, state.url, 'roles-unavailable');
  }
};

// `my-roles` отвечает `200 []` и на проект, которого нет, — «проекта нет» решает его собственный GET:
// 404 — страница 404, 403 — проект есть, роли нет; прочий сбой ничего не доказал.
// 403 здесь штатный ответ: без EXPECTED_FORBIDDEN errorInterceptor перепроверил бы личность.
async function projectRefusal(
  router: Router,
  http: HttpClient,
  projectId: string,
  returnUrl: string,
): Promise<UrlTree | RedirectCommand> {
  try {
    await firstValueFrom(
      http.get(`/api/projects/${encodeURIComponent(projectId)}`, { context: new HttpContext().set(EXPECTED_FORBIDDEN, true) }),
    );
  } catch (error) {
    const status = error instanceof HttpErrorResponse ? error.status : 0;
    if (status === 404) return new RedirectCommand(router.parseUrl(NOT_FOUND_URL), { skipLocationChange: true });
    if (status !== 403) return denied(router, returnUrl, 'roles-unavailable');
  }

  return studentExit(router, returnUrl);
}

// Любой адрес вне таблицы маршрутов: его ловит `**` — та же страница 404, что у остальных битых
// адресов; skipLocationChange оставляет в строке набранный адрес.
const NOT_FOUND_URL = '/not-found';

function isStaff(roles: Role[]): boolean {
  return roles.some(role => MANAGER_ROLES.includes(role));
}

// Гостя вести можно только на вход: роли, спрошенные до вердикта, ответят 401. Без BFF вести некуда.
function noSessionExit(router: Router, returnUrl: string): boolean | UrlTree {
  const back = requestedUrl(router) ?? returnUrl;
  if (!isBffMode()) return denied(router, back, 'no-session');
  redirectToBffLogin(back);
  return false;
}

// Именно набранный адрес: развилку «/» (кабинет или студия партнёра) редирект прошёл без сессии и угадал кабинет.
function requestedUrl(router: Router): string | null {
  const initial = router.getCurrentNavigation()?.initialUrl;
  return initial ? router.serializeUrl(initial) : null;
}

function studentExit(router: Router, returnUrl: string): UrlTree {
  const stored = readClientSession();
  const reentry = stored ? buildClientReentryUrl(stored) : null;
  return reentry ? router.createUrlTree([reentry]) : denied(router, returnUrl, 'not-staff');
}

function denied(router: Router, returnUrl: string, reason: CabinetDeniedReason): UrlTree {
  return router.parseUrl(buildCabinetDeniedUrl(returnUrl, reason));
}
